import { CSSProperties, ReactNode } from 'react';
import styled from 'styled-components';
import { IconClickable } from 'atomicComponents/IconClickable';
import { ChevronIconName } from '@ardoq/icons';
import { IconContainer, ComponentLabel } from './atoms';
import { ComponentRepresentation } from '@ardoq/basic-components';
import {
  COMPONENT_CONTEXT_ITEM_CONTAINER_BORDER,
  HEADER_CELL_VERTICAL_PADDING,
  MARGIN_PER_LEVEL,
} from '../consts';

import { classes, returnZero } from '@ardoq/common-helpers';
import { HeaderComponentData } from '../types';
import { dispatchAction } from '@ardoq/rxbeach';
import { setComponentId } from 'streams/context/ContextActions';
import {
  ACTIVE_BOX_SHADOW,
  ACTIVE_BRIGHTNESS,
  COMPONENT_ID_ATTRIBUTE,
  LI_BACKGROUND_CLASS_NAME,
} from '@ardoq/global-consts';
import { getComponentLabelParts, truncateComponentLabel } from '@ardoq/graph';
import { SelfTruncatingComponentLabel } from 'tabview/SelfTruncatingComponentLabel';
import { BULK_POPOVER_ATTR } from '@ardoq/popovers';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { colors } from '@ardoq/design-tokens';

export const resolveComponentLabelPopover = (
  element: HTMLElement | SVGElement
): ReactNode | null => {
  const componentId = element.getAttribute(COMPONENT_ID_ATTRIBUTE);
  if (!componentId) {
    return null;
  }
  return truncateComponentLabel({
    ...getComponentLabelParts(componentId),
    width: Infinity,
    measure: returnZero,
  });
};

const headerComponentLabelPopoverAttrs = {
  [BULK_POPOVER_ATTR]: 'true',
};

const IconAndLabelContainer = styled.div`
  display: flex;
  overflow: hidden;
  flex: 1;
`;

const HeaderComponentContainer = styled.div`
  display: block;
  box-shadow: 0px 0px 1px 0px;
  &.active {
    filter: brightness(${ACTIVE_BRIGHTNESS});
    box-shadow: ${ACTIVE_BOX_SHADOW};
  }
  &.component.${LI_BACKGROUND_CLASS_NAME}.context {
    border-width: ${COMPONENT_CONTEXT_ITEM_CONTAINER_BORDER}px;
    border-color: ${colors.blue600} !important;
  }
  padding-top: ${HEADER_CELL_VERTICAL_PADDING}px;
`;

const COMPONENT_MATRIX_LABEL_FONT =
  '400 14px/20px Inter, -apple-system, Segoe UI, Roboto, Noto Sans, Ubuntu, Cantarell, Helvetica Neue';
const measureHeaderComponentText = canvasLabelMeasurer(
  COMPONENT_MATRIX_LABEL_FONT
);
interface HeaderComponentArgs {
  componentId: string;
  showExpander: boolean;
  isExpanded: boolean;
  expanderClicked: () => void;
  isRowHeader: boolean;
  containerStyle: CSSProperties;
  iconContainerStyle: CSSProperties;
  isHighlighted: boolean;
  address: (string | null)[];
  headerComponentData: HeaderComponentData;
}

const HeaderComponent = ({
  componentId,
  showExpander,
  expanderClicked,
  isExpanded,
  isRowHeader,
  containerStyle,
  iconContainerStyle,
  isHighlighted,
  address,
  headerComponentData,
}: HeaderComponentArgs) => {
  const level = address.length - 1;
  const rootLevel = level === 0;
  const actualContainerStyle = {
    height:
      rootLevel || isRowHeader
        ? '100%'
        : `calc(100% - ${level * 2 * MARGIN_PER_LEVEL}px)`,
    width:
      rootLevel || isRowHeader
        ? '100%'
        : `calc(100% - ${level * 2 * MARGIN_PER_LEVEL}px)`,
    ...containerStyle,
  };

  const {
    className: classNames,
    representationData: componentRepresentationData,
    isContext,
    brightnessIncreaseFactor,
    iconColor,
  } = headerComponentData;

  if (!rootLevel) {
    if (!isRowHeader) {
      actualContainerStyle.margin = `${level * MARGIN_PER_LEVEL}px`;
    }
    const brightness =
      (1 + brightnessIncreaseFactor * 0.05) *
      (isHighlighted ? ACTIVE_BRIGHTNESS : 1);
    actualContainerStyle.filter = `brightness(${brightness})`;
  }

  const contextMenuAttribute = COMPONENT_ID_ATTRIBUTE;
  return (
    <HeaderComponentContainer
      style={actualContainerStyle}
      className={classes(
        classNames,
        isHighlighted && 'active',
        isContext && 'context'
      )}
    >
      <IconAndLabelContainer
        className="component skipContextUpdate"
        {...{ [contextMenuAttribute]: componentId }}
        onDoubleClick={() => dispatchAction(setComponentId(componentId))}
      >
        <IconContainer $color={iconColor}>
          <ComponentRepresentation
            isImage={componentRepresentationData.isImage}
            value={componentRepresentationData.value}
            icon={componentRepresentationData.icon}
          />
        </IconContainer>
        <ComponentLabel
          style={{ width: '100%' }}
          {...headerComponentLabelPopoverAttrs}
          {...{ [COMPONENT_ID_ATTRIBUTE]: componentId }}
        >
          <SelfTruncatingComponentLabel
            {...getComponentLabelParts(componentId)}
            measure={measureHeaderComponentText}
            showTruncationPopover={false}
          />
        </ComponentLabel>
        {showExpander && (
          <IconClickable
            display="inline"
            onClick={expanderClicked}
            iconName={isExpanded ? ChevronIconName.UP : ChevronIconName.DOWN}
            containerStyle={iconContainerStyle}
          />
        )}
      </IconAndLabelContainer>
    </HeaderComponentContainer>
  );
};
export { HeaderComponent };
